import { useEffect, useRef } from 'react'

/**
 * The live waveform.
 *
 * Real amplitude from the analyser, scrolling right to left, so a parent can
 * tell at a glance that the microphone is actually hearing them. That is the
 * whole job: it is feedback, not decoration, and a canned animation would be
 * worse than nothing because it would lie when the mic is dead.
 *
 * Bars are plain elements scaled on their own animation frame rather than
 * re-rendered. Sixty React renders a second during the one interaction that
 * must feel instant is not a trade worth making, and `transform` stays on the
 * compositor so the scroll costs no layout.
 */

const BARS = 44
/** Below this the line still reads as a line rather than disappearing. */
const FLOOR = 0.045

export function Waveform({
  getLevel,
  active,
}: {
  getLevel: () => number
  active: boolean
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const historyRef = useRef<number[]>(new Array(BARS).fill(0))
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const bars = Array.from(host.querySelectorAll<HTMLElement>('.waveform-bar'))
    const history = historyRef.current

    if (!active) {
      history.fill(0)
      bars.forEach((bar) => bar.style.setProperty('--v', String(FLOOR)))
      return
    }

    const draw = () => {
      history.push(Math.max(FLOOR, getLevel()))
      if (history.length > BARS) history.shift()

      for (let i = 0; i < bars.length; i++) {
        const v = history[i] ?? FLOOR
        // Ease the ends down so the shape reads as one voice rather than a
        // rectangle of noise clipped at the edges.
        const edge = Math.sin((i / (BARS - 1)) * Math.PI)
        bars[i]!.style.setProperty('--v', String(Math.max(FLOOR, v * (0.45 + edge * 0.55))))
      }
      rafRef.current = requestAnimationFrame(draw)
    }
    rafRef.current = requestAnimationFrame(draw)

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [active, getLevel])

  return (
    <div
      ref={hostRef}
      className={`waveform${active ? ' is-live' : ''}`}
      role="img"
      aria-label={active ? 'Listening' : 'Not recording'}
    >
      {/* `--i` slices one gradient across the row; it is set once, not per frame. */}
      {Array.from({ length: BARS }, (_, i) => (
        <span
          key={i}
          className="waveform-bar"
          style={{ '--i': i } as React.CSSProperties}
          aria-hidden="true"
        />
      ))}
    </div>
  )
}
